// public/v3/src/lib/consent.js — single-patient verbal-consent recording.
//
// Verbatim port of v2's markConsentSigned(id) from public/ccm-compliance.js
// (lines 1195-1257). Behavior preserved: pulls the patient's first Elation
// office-visit date as the canonical consent date, falling back to today
// when the linkage is missing or the lookup fails. Toast strings, audit
// action name, and audit detail format are unchanged.
//
// v2 → v3 adaptations (allowed-change list only):
//   - getPatient(id) global lookup → patient passed in by the caller
//   - markPatientDirty + saveAppData (in-memory cache) → ccmApi.updatePatient
//     which persists the same shape the v2 code put into appData and then
//     POSTed via /data/patients
//   - _fetchWithTimeout(API_BASE+url, opts) → ccmApi wrappers
//   - toast() / confirm() → returned as a result for the caller to surface
//     in the existing v3 toast pattern (studio-toast). The function still
//     calls window.confirm() inline to match v2's blocking confirmation —
//     the modal/toast UX wasn't requested for this iteration.
//   - currentUser / currentPracticeId — not referenced; the server scopes
//     by practice via subdomain.

import { ccmApi } from './api';

// Link-approval provenance (consentSource/consentLinkId — written only by
// server/services/consentLink/approve.js) describes the CURRENT consent
// record. Every staff write of the top-level consent field must clear it:
// a stale 'patient-link' stamp surviving a staff-recorded consent would keep
// the billing note attesting "Electronic (patient-approved via secure link)"
// for consent that was actually obtained verbally or in writing.
export function clearConsentLinkProvenance(raw) {
  delete raw.consentSource;
  delete raw.consentLinkId;
}

// Returns { ok: bool, toast: string, skipped?: 'already-signed' | 'cancelled' }
// The caller surfaces the toast string via setToast.
export async function markConsentSigned(patient) {
  const p = patient?._raw || patient;
  if (!p) return { ok: false, toast: 'Patient not found.' };
  if (p.consent === 'signed') return { ok: false, toast: 'Consent already recorded.', skipped: 'already-signed' };
  const name = (p.fname || '') + ' ' + (p.lname || '');
  if (!window.confirm('Record verbal consent for ' + name + '?\n\nThis documents that the patient (or authorized representative) verbally agreed to chronic care management services.\n\nThis action will be audit-logged.')) {
    return { ok: false, toast: '', skipped: 'cancelled' };
  }
  const prevConsent = p.consent || 'pending';

  // Practice policy: consent is obtained at the patient's first encounter,
  // so the date of the earliest Elation office visit is the canonical
  // consent date. Fall back to today only when no first-visit date is
  // available (no Elation link, no visits on file, or lookup error).
  const today = new Date().toISOString().slice(0, 10);
  let consentDate = today;
  let dateSource = 'today (fallback)';
  let fallbackReason = '';
  if (p.elation_id) {
    try {
      const data = await ccmApi.firstVisitDates([String(p.elation_id)]);
      const firstVisit = data && data.results && data.results[String(p.elation_id)];
      if (firstVisit) {
        consentDate = firstVisit;
        dateSource = 'first Elation visit';
      } else {
        fallbackReason = 'no visits on file in Elation';
      }
    } catch (e) {
      fallbackReason = 'lookup error';
      try { console.warn('First-visit lookup failed; using today:', e); } catch (_) {}
    }
  } else {
    fallbackReason = 'patient not linked to Elation';
  }

  // Persist the consent change. v2 mutated the in-memory patient + called
  // markPatientDirty/saveAppData which eventually PUTs the patient blob to
  // /data/patients/:id. In v3 we PUT directly via ccmApi.updatePatient.
  //
  // Body includes _updatedAt so the server's 409 optimistic-lock check fires
  // on stale writes (server/routes/patients.js:117-194). Capture the response's
  // new updatedAt and return it so callers can refresh their local _raw cache
  // before the next write — Studio's handleMarkConsent currently refetches the
  // whole patient list on success, so this is informational for that path; the
  // value is here so non-refetch callers (e.g. future programmatic uses) can
  // chain writes without 409s.
  const patientId = patient?.id || p.id;
  const nextRaw = { ...p, consent: 'signed', consentDate };
  clearConsentLinkProvenance(nextRaw);
  const resp = await ccmApi.updatePatient(patientId, nextRaw);
  const updatedAt = (resp && resp.updatedAt) || null;

  // v2 toast strings — preserved character-for-character.
  let toast;
  if (dateSource === 'first Elation visit') {
    toast = 'Verbal consent recorded for ' + name + ' — dated ' + consentDate + ' (first Elation visit).';
  } else {
    toast = 'No first-visit date found (' + fallbackReason + '); consent dated today (' + consentDate + ') for ' + name + '.';
  }

  // Audit the consent change. Same action name and detail format as v2.
  try {
    await ccmApi.auditEvent(
      'CONSENT_CHANGE',
      'patients',
      patientId,
      'Consent: ' + prevConsent + ' → signed (verbal) for ' + name + ' — date ' + consentDate + ' from ' + dateSource
    );
  } catch (_) { /* v2 ignored audit errors with .catch(function(){}) */ }

  return { ok: true, toast, consentDate, dateSource, updatedAt };
}
